import { call } from '@advanza/api'
import { Divider } from '@advanza/ui'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import Card from 'components/Card'
import { useEffect, useState } from 'react'

export const CHATBOT_DPA_ACCEPTANCES_ENDPOINT = 'office/providers/get-chatbot-dpa-acceptances'

export interface ChatbotDpaAcceptance {
    acceptanceId: number
    acceptedAt: string
    userId: number
    userEmail: string
    version: string
    market: string
    source: 'setup' | 'login_modal' | 'settings'
}

interface GetChatbotDpaAcceptancesResponse {
    acceptances: ChatbotDpaAcceptance[]
}

const SOURCE_LABEL: Record<ChatbotDpaAcceptance['source'], string> = {
    setup: 'Chatbot setup',
    login_modal: 'Login modal',
    settings: 'Chatbot settings',
}

interface ChatbotDpaWidgetProps {
    providerId: number
}

/**
 * Read-only record of the chatbot processing agreement (verwerkersovereenkomst)
 * acceptances, so CS and legal can check which version a provider accepted.
 */
const ChatbotDpaWidget = ({ providerId }: ChatbotDpaWidgetProps) => {
    const [acceptances, setAcceptances] = useState<ChatbotDpaAcceptance[] | null>(null)
    const [error, setError] = useState(false)

    useEffect(() => {
        let cancelled = false
        setAcceptances(null)
        setError(false)

        call(`${CHATBOT_DPA_ACCEPTANCES_ENDPOINT}/${providerId}`)
            .then((response: GetChatbotDpaAcceptancesResponse) => {
                if (!cancelled) {
                    setAcceptances(response.acceptances)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setError(true)
                }
            })

        return () => {
            cancelled = true
        }
    }, [providerId])

    const content = (() => {
        if (error) {
            return <p className="text-md text-neutral-500">Could not load DPA acceptances.</p>
        }
        if (acceptances === null) {
            return <LoadingDots />
        }
        if (acceptances.length === 0) {
            return <p className="text-md text-neutral-500">The DPA has not been accepted yet.</p>
        }
        const [latest, ...history] = acceptances
        return (
            <div className="space-y-3 text-md">
                <dl className="grid grid-cols-2 gap-y-2">
                    <Field label="Accepted at" value={formatMoment(latest.acceptedAt)} />
                    <Field label="Version" value={latest.version} />
                    <Field label="Accepted by" value={`${latest.userEmail} (#${latest.userId})`} />
                    <Field label="Market" value={latest.market} />
                    <Field label="Source" value={SOURCE_LABEL[latest.source]} />
                </dl>
                {history.length > 0 && (
                    <details>
                        <summary className="cursor-pointer text-sm text-neutral-500">
                            Earlier acceptances ({history.length})
                        </summary>
                        <ul className="mt-2 space-y-1 text-sm text-neutral-700">
                            {history.map((acceptance) => (
                                <li key={acceptance.acceptanceId}>
                                    {formatMoment(acceptance.acceptedAt)} — {acceptance.version} —{' '}
                                    {acceptance.userEmail}
                                </li>
                            ))}
                        </ul>
                    </details>
                )}
            </div>
        )
    })()

    return (
        <Card id="chatbot-dpa-widget" icon="gavel" header="Chatbot DPA">
            <Divider m />
            {content}
        </Card>
    )
}

interface FieldProps {
    label: string
    value: string
}

const Field = ({ label, value }: FieldProps) => (
    <div>
        <dt className="text-[10px] text-neutral-400 uppercase tracking-wide">{label}</dt>
        <dd className="text-neutral-900 break-words">{value}</dd>
    </div>
)

const formatMoment = (value: string): string => new Date(value).toLocaleString('nl-NL')

export default ChatbotDpaWidget
